export { ChainCell }
export type {IChannel, IChainCell, IChainCellState}
import { IMenuChoice } from "./menu"
import { IMidiNote } from "./models/midi"
import { ITag, ITagController } from "./models/tag"
import { IArch, ChannelPort, PortId } from "./arch"

const D = (...args) => window.console.log(...args)
const E = console.error

export type TItemChannel = 'L' | 'R'
export type TChanExcl = { L: true, R?: undefined } | { R:true, L?: undefined } 

export type TRecModuleDef = Record<string,IModuleDef>
export type TRecDeviceDef = Record<string,IDeviceDef>
export type TRecChain = IChain[]

export type TModuleRole =
    {source: true, dest?: undefined, proc?: undefined}
    | {dest: true, source?: undefined, proc?: undefined}
    | {proc: true, source?: undefined, dest?: undefined}

export type TPhonia =
    {stereo: true, m2s?: undefined, mono?: undefined }
    | {m2s: true, stereo?: undefined, mono?: undefined }
    | {mono: true, stereo?: undefined, m2s?: undefined }

export type TRoleType = 'source' | 'dest' | 'proc'
export type TPhoniaType  = 'mono' | 'stereo' | 'm2s'

export type TAlertArgs = {
    title: string
    message: any
    dismiss?: string
}

export interface ISchemaVer {
    major: number
    minor: number
    patch: number
}

export const SCHEMA_VER : ISchemaVer = {
    major: 2,
    minor: 0,
    patch: 0,
}

export interface IModuleDefController {
    gotoDevice (id: string) : void
}

export interface IModuleController {
    midi: {
        assignment?: IMidiNote
        choices () : IMenuChoice[]
    }
    gotoDevice () : void
}

export interface IDeviceDefController {
    set (devices: Record<string,IDeviceDef>)
    get () : Record<string,IDeviceDef>
}

interface IRoute {
    from: number
    to: number
}

export interface IRoled {
    source?: boolean
    proc?: boolean
    dest?: boolean
}

export class Roled {
    /**
     *  ROLE of module/modules
     */
    static create({source=false,proc=false,dest=false}:{source?:boolean,proc?:boolean,dest?:boolean}) 
        : IRoled
    {
        return { source,proc,dest }
    }
}

export class Phonia {
    static M2S:TPhonia = { m2s: true }
    static MONO:TPhonia = { mono: true }
    static STEREO:TPhonia = { stereo: true }

    static fromType (type: TPhoniaType) : TPhonia | undefined {
        return type === 'mono'
            ? Phonia.MONO
            : type === 'stereo'
            ? Phonia.STEREO
            : type === 'm2s'
            ? Phonia.M2S
            : undefined
    }

    static fromString (type: string) : TPhonia | undefined {
        return Phonia.fromType(type as TPhoniaType)
    }

    static same (a:TPhonia,b:TPhonia) : boolean {
        return (a.mono && a.mono === b.mono)
            || (a.stereo && a.stereo === b.stereo)
            || (a.m2s && a.m2s === b.m2s)
            || false
    }

    static toString (self: TPhonia) : string {
        return self.m2s ? 'M2S'
            : self.stereo ? 'Stereo'
            : 'Mono'
    }

    static all () : TPhonia[] {
        return [
            {mono: true},
            {stereo: true},
            {m2s: true},
        ]
    }
}

// User-supplied background image for the desktop (#space) area.
// The image is referenced by URL; only this lightweight descriptor is persisted.
export interface IBackgroundSettings {
    enabled: boolean
    url?: string
    fit: 'cover' | 'contain' | 'tile'
    posX: number    // horizontal focus, 0-100 %
    posY: number    // vertical focus, 0-100 %
    opacity: number // image opacity, 0-1
    dim: number     // black scrim over the image, 0-1
    blur: number    // gaussian blur, px
}

export interface IAppSettings {
    midi?: {
        port?: {id: string, name: string}  // Deprecated: for migration from single-port
        ports?: {id: string, name: string}[]  // Selected ports (multi-select)
    },
    mute: {
        midi?: IMidiNote
    },
    offlineWarn?: boolean
    background?: IBackgroundSettings
}

export interface IAppState {
    settings: IAppSettings
    cond: {
        muteAll?: boolean
    }
}

export class SupportPayload {

    static build ({
        ts,
        contact,
        message,
        env,
        sessionFile,
    }: {
        ts: string
        contact: { name: string, email: string }
        message: string
        env: {
            serial: string | null
            hw_model: string
            paw: { version: string, channel: string, build: string }
            wolffhound: string | null
            system: Record<string, string> | undefined
        }
        sessionFile?: {
            name: string
            data: any
        }
    }) {
        return {
            version: '1.1',
            ts,
            contact,
            message: message || '',
            env: {
                serial: env.serial,
                hw_model: env.hw_model,
                paw: env.paw,
                wolffhound: env.wolffhound,
                ...env.system ?? {},
            },
            sessionFile,
        }
    }
}

export class AppState {

    static create () : IAppState {
        return {
            settings: {
                mute: {},
                offlineWarn: true,
            },
            cond: {
            },
        }
    }
}

export class ModuleRole {

    static fromType (type: TRoleType) : TModuleRole | undefined {
        return type === 'source'
            ? { source: true }
            : type === 'proc'
            ? { proc: true }
            : type === 'dest'
            ? { dest: true }
            : undefined
    }

    static toType (self: TModuleRole) : TRoleType {
        return self.source 
            ? 'source'
            : self.proc
            ? 'proc'
            : 'dest'
    }

    static fromString (type: string) : TModuleRole | undefined {
        return ModuleRole.fromType(type as TRoleType)
    }

    static same (a:TModuleRole,b:TModuleRole) : boolean {
        return (a.dest && a.dest === b.dest)
            || (a.source && a.source === b.source)
            || (a.proc && a.proc === b.proc)
            || false
    }

    static toString (self: TModuleRole) : string {
        return self.proc ? 'Processor'
            : self.source ? 'Source'
            : 'Destination'
    }

    static all () : TModuleRole[] {
        return [
            {source: true},
            {proc: true},
            {dest: true},
        ]
    }
}

export class Role {
    static create (role:IRoled) : IRoled {
        return {
            proc: role.proc,
            dest: role.dest,
            source: role.source,
        }
    }

    static isSame (a:IRoled,b:IRoled) : boolean {
        return this.toString(a) === this.toString(b)
    }

    static toString (role:IRoled) : string | undefined {
        return role.dest ? 'dest'
        : role.proc ? 'proc'
        : role.source ? 'source'
        : undefined
    }
}

export interface IChainController {
    add () : IChain
    all (): TRecChain
    modify (
        {id,state,cond,chain,mutate}
        : {
            id?: string
            cond?: IChainCondition
            state?: IChainControllerState
            chain?: IChain
            mutate?: boolean
        }) : void
    name (id:string,name:string) : void
    color (id:string,color:string) : void
    remove (id:string) : void,
    select (id:string) : void
    active (id:string,value?:boolean) : void
    focus (id:string,focus:boolean) : void
    hide (id:string) : void
    show (id:string,leaveTarget:boolean) : void
    fave (id:string) : void
    lock (id:string) : void
    normal (id:string) : void
    split (id:string) : void
    unsplit (id:string) : void
    copy (id:string,idgen:() => string) : void
    tags (id:string) : ITagController
}

export interface IChainBoxController {
    set (state:IChainState,cond:IChainCondition) : void
    name (name:string) : void
    color (color:string) : void
    remove () : void
    active () : void
    focus (focus?:boolean) : void
    hide () : void
    show (leaveTarget:boolean) : void
    fave () : void
    lock () : void
    normal () : void
    copy () : void
    alert (args: TAlertArgs) : void
    midi: {
        assignment?: IMidiNote
        choices: () => IMenuChoice[]
    }
    // TOGGLE: BYPASS/RESTORE processors
    bypass?: () => void
}

export interface IChainHeaderController extends IChainBoxController {
    copy () : void
    split? () : void
    unsplit? () : void
    tags: ITagController
}

interface IChainCellState {
    active: {
        L?: number
        R?: number
    }
}

export class ChainCellState {
    static create (role:IRoled) : IChainCellState {
        return {
            // DEFAULT ON state (both channels)
            active: {L: 0, R: 0}
        }
    }
}

export interface IChainCondition {
    active?:boolean
    selected?:boolean
    focus?:boolean
    hidden?:boolean
    new?:boolean
    bypass?: Record<string, { L?: number; R?: number }> | false
}

export interface IChainStateBase {
    fave?:boolean
    normal?:boolean
    locked?:boolean
    midi?: {
        activate?: IMidiNote
    }
}

export interface IChainState extends IChainStateBase {
    color:string
    name: string
    tags: ITag[]
}

export interface IChainControllerState extends IChainStateBase {
    name?: string
    color?:string
    tags?:ITag[]
}

const clone = (_:any) => JSON.parse(JSON.stringify(_))


interface IChannel {
    port: number
    to: boolean
    from: boolean
    portId?: PortId
    side?: string
}

/* physical I/O port belonging to a device */
export class Channel {
    static create ({to,from,side,arch}:
    {
        to?: string|number
        from?:string|number
        side?: string
        arch: IArch
    })
    : IChannel
    {
        if (to != null && from != null) {
            throw new Error('Channel must be exclusively input or output')
        }
        if (to == null && from == null) {
            throw new Error('Channel has no defined input/output')
        }
        const $port = to != null ? to! : from!
        // fromPortId accepts string or number and validates against arch
        const port = ChannelPort.fromPortId($port, arch)
        if (port === undefined) {
            throw new Error(`Invalid port for ${arch.model.name}: ${$port}`)
        }
        return {
            port,
            to: to != null,
            from: from != null,
            side,
        }
    }

    static valid (self: IChannel,arch:IArch) : boolean {
        const {to,from,port} = self
        return (to !== from)
            && port != null
            && arch.ports.isValid(port)
    }
}

export type TLatori = 
    {L:true,R?:false}
    |
    {R:true,L?:false}


export interface IChannelPairDef {
    from?: IChannel
    to?: IChannel
}

export type TChannelPair = {
        from: IChannel
        to: IChannel
    }
    | {
        from: IChannel
        to?: undefined
    }
    | {
        from?: undefined
        to: IChannel
    }

class ChannelPairDef {
    static valid (self: IChannelPairDef, role: TModuleRole, arch: IArch) : boolean {
        // PROPER ports defined for given role
        const roled = role.proc
            ? !!(self.from && self.to)
            : role.source
            ? !!self.from
            : !!self.to
        if (!roled) return false
        return (!self.from || Channel.valid(self.from,arch)) && (!self.to || Channel.valid(self.to,arch))
    }

    static createChannelPair (self: IChannelPairDef, role: TModuleRole, arch: IArch) : TChannelPair | undefined {
        const {to,from} = self
        return !ChannelPairDef.valid(self,role,arch)
            ? undefined
            : role.proc
            ? {
                to: to!,
                from: from!,
            }
            : role.source
            ? { from: from! }
            : { to: to! }
    }
}

export interface TModulePair {
    L?: IModule
    R?: IModule
}

export interface TStatusPair {
    L?: boolean
    R?: boolean
}


export type TDevNameOpts = {
    mfr?: boolean
    prod?: boolean
    name?: boolean
}

export interface IDeviceDef {
    id: string
    name: string
    product: {
        name?: string
        mfr?: string
    }
    nodes: TRecModuleDef
    nodeOrder?: string[] // persists user-defined node order (DND)
    notes?: string
}

export interface IDevice {
    id: string
    name: string
    notes?: string
    product: {
        name?: string
        mfr?: string
    }
}

export class DeviceDef {

    static copy ({self, idgen, name}: {self: IDeviceDef, idgen: () => string, name: string}) : IDeviceDef {
        const {
            product,
            nodes,
            notes,
        } = self


        const copy : IDeviceDef  = {
            id: idgen(),
            name,
            // Deep clone product to ensure session independence
            product: { ...product },
            nodes: {},
            notes,
        }
        const device = Device.fromDef(copy)
        // COPY NODES
        copy.nodes = Object.values(nodes).reduce((p,node) => {
            const id = idgen()
            const copy = ModuleDef.copy({self:node,id: id})
            // REFRESH device
            copy.device = clone(device)
            p[id] = copy
            return p
        },{})

        return copy
    }

    static getUniqueName (
        { id, mfr, prod, devices:$devices, } :
        {
            id?:string
            mfr?:string
            prod?:string
            devices:TRecDeviceDef
    }) : string | undefined
    {
        const devices = {...$devices}
        if (id) {
            // REMOVE current device from consideration
            delete devices[id]
        }

        // PROSPECTIVE name
        const name = mfr && prod ? `${mfr} ${prod}` : mfr ? mfr : prod
        // IF no manufacturer or product name to work with just default to name
        if (!name) return
        // CANONICALIZE existing names
        const names = Object
            .values(devices)
            .map(({name}) => name)
            .reduce((p,c) => {
                p[c.toLowerCase()] = c
                return p
            },{})
        // CONFLICT?
        if (names[name.toLowerCase()]) {
            // DERIVE SUFFIX resolving conflict
            const suffix = Object
                .keys(names)
                // PREFIX MATCH ostensibly
                .filter(_name => _name.startsWith(name.toLowerCase()))
                // EXACT match or PREFIX MATCH + INDEX (e.g. ABC Reverb 1)
                .filter(_name => _name.substring(name.length).match(/\s+\d+$/))
                // DERIVE suffixes
                .map(_name => +_name.match(/\d+$/)![0])
                // DERIVE largest existing suffix
                .reduce((p,suffix) => {
                    if (+suffix > p) p = +suffix
                    return p
                },-1)
            
            const index = suffix > 0 ? suffix + 1 : 1
            return `${name} ${index}`
        }
        else {
            return name
        }
    }

    /**
     * Sort devices by name, sorting numerically on numeric suffixes
     * @param modules 
     * @return sorted, shallow array copy
     */
    static sortByName (devices: IDeviceDef[]) {
        return [...devices].sort((a,b) => {
            // GROUP by DEVICE name first
            const sortDev = Names.sorter(a.name,b.name)
            return sortDev === 0
                // SORT by MODULE name (device name match)
                ? Names.sorter(a.name,b.name)
                : sortDev
        })
    }

    /**
     * COLLECT nodes of devices
     * @param devices
     * @param valid valid nodes only
     */
    static extractNodes (devices: TRecDeviceDef|IDeviceDef[]) : IModuleDef [] {
        return Object.values(DeviceDef.extractNodesIndexed(devices))
    }

    /**
     * INDEX nodes of devices
     * @param devices
     * @param valid valid nodes only
     */
    static extractNodesIndexed (devices: TRecDeviceDef|IDeviceDef[]) : TRecModuleDef {
        return (Array.isArray(devices) ? devices : Object.values(devices))
            .reduce((p,device) => {
                Object
                .values(device.nodes)
                .forEach(node => p[node.id] = node)
                return p
            },{})
    }
}

export class Device {

    static create ({
            id,
            name,
            product,
            notes,
        } : {
            id: string
            name: string
            product: {
                name?: string
                mfr?: string
            }
            notes?: string
        }) : IDevice {
        
        return {
            id,
            name,
            product,
            notes,
        }
    }

    static fromDef (self: IDeviceDef) : IDevice {
        return Device.create({
            id: self.id,
            name: self.name,
            product: self.product,
            notes: self.notes,
        })
    }

    static fromDefs (other: IDeviceDef[]) : IDevice[] {
        return other.map(this.fromDef)
            .filter(_ => _)
            .map(_ => _!)
    }
}

/*
 * A logically grouped set of channels (having same device)
 */
export interface IModuleDef {
    id: string
    device: IDevice
    name: string
    role: TModuleRole
    phonia: TPhonia
    L: IChannelPairDef
    R: IChannelPairDef
    tags: ITag[]
}

export class ModuleDef {

    static copy ({self, id, name}: {self: IModuleDef, id: string, name?: string}) : IModuleDef {
        const {
            device,
            role,
            phonia,
            L,
            R,
            tags,
        } = self

        return {
            id,
            name: name ?? self.name,
            device,
            role,
            phonia,
            L: L ? clone(L) : L,
            R: R ? clone(R) : R,
            tags,
        }
    }

    /**
     * Sort modules by name, sorting numerically on numeric suffixes
     * @param modules 
     * @return sorted, shallow array copy
     */
    static sortByName (modules: IModuleDef[]) {
        return [...modules].sort((a,b) => {
            // GROUP by DEVICE name first
            const sortDev = Names.sorter(a.device.name,b.device.name)
            return sortDev === 0
                // SORT by MODULE name (device name match)
                ? Names.sorter(a.name,b.name)
                : sortDev
        })
    }

    /**
     * All necessary ports defined for module mode?
     */
    static valid (self: IModuleDef, arch: IArch) : boolean {
        const {L,R,phonia,role} = self
        return !!(
            phonia.mono && L && ChannelPairDef.valid(L,role,arch)
            || phonia.stereo && L && R && ChannelPairDef.valid(L,role,arch) && ChannelPairDef.valid(R,role,arch)
            || phonia.m2s && role.proc && L && R && ChannelPairDef.valid(L,role,arch) && ChannelPairDef.valid(R,{source:true},arch)
        )
    }
}

/*
 * A logically grouped set of channels (having same device)
 */
export interface IModule {
    id: string
    name: string
    device: IDevice
    role: TModuleRole
    m2s?: boolean
    proc?: boolean
    dest?: boolean
    source?: boolean
    stereo?: boolean
    mono?: boolean
    phonia: TPhonia
    L: TChannelPair
    R?: TChannelPair
    channels: IChannel[]
    midi?: {
        activate?: {
            L?: IMidiNote
            R?: IMidiNote
        }
    }
    tags: string[]
    invalid?: boolean
}

export class Names {
    /**
     * APPLY some reasonable transformations to make name "filesafe" across platforms.
     * Default is applied if after tranforms supplied name is effectively blank.
     * @param name 
     * @param $default 
     * @returns "filesafe" nane
     */
    static makeFileSafe (name:string,$default:string) : string {
        // RESERVED/UNSAFE characters and names
        const unsafeRegex = /[<>:"/\\|?*\x00-\x1F]/g // (includes control chars)
        const reserved = /^(CON|PRN|AUX|NUL|COM[0-9]|LPT[0-9])(\.|\s|$)/i
    
        // TRIM spaces and OMIT leading/trailing periods
        let safeName = name.trim().replace(/^\.+|\.+$/g, '')
    
        // SUBSTITUTE unsafe characters
        safeName = safeName.replace(unsafeRegex, '_')
    
        // ESCAPE reserved name
        if (reserved.test(safeName)) {
            safeName = '_' + safeName
        }
    
        // COLLAPSE multiple underscores (due to above transformations)
        safeName = safeName.replace(/_+/g, '_')
    
        // LIMIT to 64 characters
        safeName = safeName.slice(0, 64)

        return safeName.length && safeName !== '_' ? safeName : $default
    }

    /**
     * Natural-order name collator
     * + numeric: digit runs compare as numbers wherever they appear (leading, embedded, trailing)
     * + caseFirst lower: case-insensitive primary order; lowercase wins exact-name ties
     */
    private static readonly collator = new Intl.Collator(undefined, {numeric: true, caseFirst: 'lower'})

    /**
     * Sorts names "naturally":
     * + Digit runs compare numerically wherever they appear ("Apollo 2 Ch 9" < "Apollo 2 Ch 10")
     * + Letters compare case-insensitively; lowercase precedes uppercase on otherwise-equal names
     * + Zero-padding ties ("Apollo 9" vs "Apollo 09") break to the shorter (unpadded) name
     * @param $a name A
     * @param $b name B
     */
    static sorter ($a:string, $b:string) : number {
        const order = Names.collator.compare($a,$b)
        if (order !== 0) return order
        // COLLATOR tie on distinct strings (zero-padded numbers): fewer digits first
        if ($a.length !== $b.length) return $a.length - $b.length
        // DETERMINISTIC final fallback
        return $a < $b ? -1 : $a > $b ? 1 : 0
    }

    /**
     * GENERATE unique *indexed* GIVEN origin name AND all EXISTING names in NAMESPACE
     * @param name origin name
     * @param names all names within namespace INCLUDING origin
     * @returns 
     */
    static indexedName ({name,names} : {
        name: string
        names: string[]
    }) : { origin?: string, copy: string } {

        // PRODUCE name for given index (of current form)
        function indexedName (index: number) {
            const $index = padn ? `${index}`.padStart(padn,'0') : `${index}`
            return `${prefix ?? ''}${$index}${suffix ?? ''}`
        }

        /**
         * GENERATE REGEX to match any indexed name of current form
         */
        function indexRE ({index,prefix,suffix} : {index?: string, prefix?: string, suffix?: string}): RegExp {
            // CAPTURE NEEDED zero-padding
            const indexRE = padn ? `0*\\d{${padn},}` : '\\d+'
            // ESCAPE special regex chars
            const escRE = (s?: string): string => s ? s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : ''
            const pattern = `^${escRE(prefix)}\\b(${indexRE})\\b${escRE(suffix)}$`
            return new RegExp(pattern)
        }

        // CAPTURE name COMPONENTS
        const m = 
            // NAME of form "1" or "AB 1"
            name.match(/(.*)\b(\d+)$/) ??
            // INAME of form "A 1 B"
            name.match(/(.*?)\b(\d+)\b(.+)$/)
        const [prefix,index,suffix] =
            m ? [m[1],m[2],m[3]]
            // BASE-form (e.g. 'AB')
            : [`${name} `] // APPEND whitespace

        // MIN-WIDTH format? (e.g. `001`)
        const padn = index?.startsWith('0') ? index.length : 0
        // CAPTURE matching indexes
        const re = indexRE({prefix,index,suffix})
        // CAPTURE and SORT all indexes for name format
        const indexes = names
            .map(_ => _.match(re)?.[1])
            .filter(_ => _).map(_ => _!)
            .sort((a,b) => +a - +b)
        
        return !index?.length && !indexes.length
            // ORIGIN is BASE-form and NONE INDEXES
            ? {
                origin: indexedName(1),
                copy: indexedName(2),
            }
            // ORIGIN IS (indexed OR BASE-form) and SOME indexes
            : {
                // NEXT HIGHEST index - origin UNCHANGED
                copy: indexedName(+indexes[indexes.length - 1] + 1)
            }
    }
}

export class Module {

    static create (
    {
        id,
        name,
        role,
        phonia,
        device,
        L,R,
        tags,
    } :
    {
        id:string
        name:string
        device: IDevice
        role: TModuleRole
        phonia: TPhonia
        L: TChannelPair
        R?: TChannelPair
        tags: string[]
    })
    : IModule | undefined
    {
        const channels : Array<IChannel> = [L.from,L.to,R?.from,R?.to].filter(_ => _ !== undefined)
        return {
            id,
            name,
            device,
            proc: role.proc,
            dest: role.dest,
            source: role.source,
            stereo: phonia.stereo,
            m2s: phonia.m2s,
            mono: phonia.mono,
            phonia,
            role,
            L, R,
            channels,
            tags,
        }
    }

    static fullName (self: IModule) : string {
        return `${self.device.name} ${self.name}`
    }

    // CREATE list of unique tokens found in module
    static tokenize (self:IModule) : string[] {
        return [
            ...self.name.split(/\W+/),
            ...self.device.name.split(/\W+/),
            ...self.tags.map(_ => _.split(/\W+/)).flat(),
        ]
    }

    static fromDef (other: IModuleDef, arch: IArch) : IModule | undefined {
        if (ModuleDef.valid(other,arch)) {
            const {id,name,role,phonia,tags,device} = other
            const L = ChannelPairDef.createChannelPair(other.L!,role!,arch)!
            const R = other.R && (
                phonia?.m2s
                // M2S is a special case, technically it's a proc/source hybrid (right-channel is a source)
                ? ChannelPairDef.createChannelPair(other.R,{source: true},arch)!
                : ChannelPairDef.createChannelPair(other.R,role!,arch)
            )
            return Module.create({
                id,
                device,
                name: name!,
                role: role!,
                phonia: phonia!,
                L,
                R,
                tags: tags.map(({name}) => name),
            })
        }
    }

    static fromDefs (other: IModuleDef[], arch: IArch) : IModule[] {
        return other.map(mod => this.fromDef(mod,arch))
            .filter(_ => _)
            .map(_ => _!)
    }

    /**
     * Sort modules by name, sorting numerically on numeric suffixes
     * @param modules 
     * @return sorted, shallow array copy
     */
    static sortByName (modules: IModule[]) {
        return [...modules].sort((a,b) => {
            // GROUP by DEVICE name first
            const sortDev = Names.sorter(a.device.name,b.device.name)
            return sortDev === 0
                // SORT by MODULE name (device name match)
                ? Names.sorter(a.name,b.name)
                : sortDev
        })
    }

    /**
     * EXTRACT port IDs used by module
     */
    static ports (self: IModule, arch:IArch) : {
        from: string[],
        to: string[],
    }
    {
        const {L,R} = self
        return {
            from: [L.from ? ChannelPort.toPortId(L.from.port,arch) : null,
                R?.from ? ChannelPort.toPortId(R.from.port,arch) : null].filter(_=>_) as string[],
            to: [L.to ? ChannelPort.toPortId(L.to.port,arch) : null,
                R?.to ? ChannelPort.toPortId(R.to.port,arch) : null].filter(_=>_) as string[],
        }
    }

    /**
     * REPORT any port conflicts, direct or indirect, between nodes (for given architecture)
     */
    static conflicting (a: IModule, b: IModule, arch: IArch) : boolean {
        if (a === b) return false
        const a_ports = Module.ports(a,arch)
        const b_ports = Module.ports(b,arch)
        // CONFLICT between a/b port collections?
        const conflict = (a:string[],b:string[]) => a.some(port => b.some($port => arch.ports.isConflict(port,$port)))

        return conflict(a_ports.from,b_ports.from) || conflict(a_ports.to,b_ports.to)
    }

    static getItemModules (item:TModulePair) : IModule[] {
        return [item.L,item.R].filter(_ => _) as IModule[]
    }

    /**
     * GUARANTEE module is valid for current architecture
     * @param self 
     * @param arch 
     */
    static invalid (self: IModule, arch: IArch) : boolean {
        const {L,R} = self
        // XXX use Module.ports for this
        const from = [L.from ? L.from.port : null, R?.from ? R.from.port: null].filter(_=> _ != null) as number[]
        const to = [L.to ? L.to.port : null, R?.to ? R.to.port: null].filter(_=> _ != null) as number[]
        return (!!from.length && !from.every(arch.ports.isValid)) || (!!to.length && !to.every(arch.ports.isValid))
    }
}

/**
 * Subset of channel(s) representing left-or-right side of module
 */

export interface ISubModule extends IModule {
    place: TLatori
    gid: string
}

export class SubModule {
    /**
     * Create a "split" of stereo module
     * XXX enforce stereo-ness
     * @param param0 
     * @returns 
     */
    static create ({module,place}: {
        module:IModule
        place:TLatori
    })
    : ISubModule {
        const pair = place.R ? module.R! : module.L
        return {
            ...module,
            L: pair,
            R: undefined,
            id: `${module.id}_${place.L ? 'L': 'R'}`,
            place,
            gid: module.id,
            stereo: false,
            mono: true,
            phonia: {mono: true},
            midi: undefined
        }
    }

    // DISCOVER id of stereo parent, if sub module
    static getSuper (other:IModule) : string | undefined {
        return 'gid' in other ? (other as ISubModule).gid : undefined
    }

    /* Split stereo module into mono left/right submodules */
    static split (other:IModule) : {L:IModule,R:IModule} | undefined {
        if (!this.is(other) && (other.stereo || other.m2s)) {
            return {
                L: SubModule.create({module: other,place:{'L':true}}),
                R: SubModule.create({module: other,place:{'R':true}}),
            }
        }
    }

    static is (other:IModule) : boolean {
        return !!this.getSuper(other)
    }

    // IF SUBMODULE, get channel orientation
    static getPlace (other:IModule) : 'L' | 'R' | undefined {
        if (this.is(other)) {
            const self = other as ISubModule
            return self.place.L ? 'L' : 'R'
        }
    }

    /**
     * Copy a submodule by reconstructing it with a new parent module
     * Preserves the channel placement (L or R) from the original
     */
    static copy ({oldSubModule, newParentModule}: {
        oldSubModule: ISubModule
        newParentModule: IModule
    }): ISubModule {
        return SubModule.create({
            module: newParentModule,
            place: oldSubModule.place
        })
    }
}

/**
 * Master class for copying modules of any type (regular or sub)
 * Routes to appropriate copy method based on module type
 */
export class SuperModule {
    /**
     * Copy a module (regular or submodule) using an ID mapping
     * Handles reconstruction from IModuleDef with proper submodule handling
     * @param oldModule - The original module to copy
     * @param nodeIdMap - Mapping of old node IDs to new node IDs
     * @param newNodeById - Lookup for new node definitions by ID
     * @param arch - Architecture for module validation
     * @returns The copied module with new IDs, or undefined if mapping fails
     */
    static copy ({oldModule, nodeIdMap, newNodeById, arch}: {
        oldModule: IModule
        nodeIdMap: Record<string, string>
        newNodeById: Record<string, IModuleDef>
        arch: IArch
    }): IModule | undefined {
        // Check for submodule: IDs like "baseId_L" or "baseId_R"
        const subMatch = oldModule.id.match(/^(.*)_(L|R)$/)
        const oldBaseId = subMatch ? subMatch[1] : oldModule.id

        // Map old base ID to new base ID
        const newBaseId = nodeIdMap[oldBaseId]
        if (!newBaseId) return undefined

        const newNodeDef = newNodeById[newBaseId]
        if (!newNodeDef) return undefined

        const newModule = Module.fromDef(newNodeDef, arch)
        if (!newModule) return undefined

        // Reconstruct submodule if the original was a submodule
        if (SubModule.is(oldModule)) {
            return SubModule.copy({
                oldSubModule: oldModule as ISubModule,
                newParentModule: newModule
            })
        }

        return newModule
    }
}

export interface IChainCellItem extends TModulePair {
    [key: number]: undefined 
    split: boolean
}

export class ChainCellItem {
    // COMPLETELY empty?
    // XXX ignoring `split` here, does that matter???
    static isEmpty (item: IChainCellItem) : boolean {
        return !item.L && !item.R
    }

    // FULL?
    static isWhole (item: IChainCellItem) : boolean {
        return item.split ? !!item.L && !!item.R : !!item.L
    }

    // STUB?
    static isStub (item: IChainCellItem) : boolean {
        return item.split ? !item.L && !item.R : !item.L
    }

    // IS PARTITAL FULL (not empty and not full)
    static isPartial (item: IChainCellItem) : boolean {
        return !this.isWhole(item) && !this.isEmpty(item)
    }

    static isBridged (item: IChainCellItem) : boolean {
        return item.split && !!item.L && item.L.id === item.R?.id && !!item.L.mono
    }

    static create ({L,R,split}:IChainCellItem) : IChainCellItem {
        return {L,R,split}
    }

    static createStub (split:boolean) : IChainCellItem {
        return {split}
    }
    
    static isConjoined (item: IChainCellItem) : boolean {
        return item.split && !!item.L && !!item.L.stereo && item.L.id === item.R?.id
    }

    static isStereo (item: IChainCellItem) : boolean {
        return !item.split && !!item.L && !!item.L.stereo
    }

    static isMono (item: IChainCellItem) : boolean {
        return !item.split && !!item.L && !item.L.stereo
    }

    static isM2S (item: IChainCellItem) : boolean {
        return !!item.L?.m2s
    }

    static modules ({item,split}:{item:IChainCellItem,split?:boolean}) : IModule[] {
        const modules = [item.L,item.R].filter(_ => _) as IModule[]
        return split ?
            [
                ...modules,
                // SPLIT any stereo modules into MONO equivalents
                ...(
                    modules.map(module => SubModule.split(module)).filter(_ => _ !== undefined)
                    .map(({L,R}) => [L,R]).flat()
                )
            ]
            : modules
    }

    static split (item:IChainCellItem) {
        if (!item.split) {
            item.split = true
            // SPLIT stereo module
            if (item.L?.stereo) {
                // CONJOINED
                item.R = item.L
            }
        }
    }

    static unsplit (item:IChainCellItem) {
        if (item.split) {
            item.split = false
            const L = item.L
            // CONJOINED STEREO module? SPLIT and keep LEFT channel
            if (L && L.stereo && L.id === item.R?.id) {
                item.L = SubModule.split(L)!.L
            }
            // DISCARD right channel
            item.R = undefined
        }
    }
}

/**
 * Logical "cell" of module(s) for use in chain model.
 */
interface IChainCell extends IRoled {
    id: string
    state: IChainCellState
    items: Array<IChainCellItem>
    ab?: boolean
    split: boolean
    midi?: IMidiNote
    [key: number]: undefined
}

class ChainCell {
    /**
     * Create cell from cell item(s)
     */
    static create (
    {
        id,
        items,
        state,
        source=false,
        proc=false,
        dest=false,
        ab=false,
        split,
    }
    : {
        id:string|(()=>string),
        items?:Array<IChainCellItem>
        state?:IChainCellState
        source?:boolean
        proc?:boolean
        dest?:boolean
        ab?:boolean
        split:boolean
    })
    : IChainCell
    {
        return {
            id: typeof id === 'string' ? id : id(),
            state: state || ChainCellState.create({proc,source,dest}),
            items: items || [],
            ab,
            split,
            ...Roled.create({source,proc,dest}),
        }
    }

    /**
     * CONTAINS M2S module?
     * @param cell 
     * @returns 
     */
    static hasM2S (cell: IChainCell) : IModule | undefined {
        return cell.items[0].L?.m2s
            ? cell.items[0].L
            : undefined
    }

    /**
     * SPLIT cell
     */
    static split (cell:IChainCell) {
        if (!cell.split) {
            cell.split = true
            cell.items.forEach(ChainCellItem.split)
        }
    }

    /**
     * 
     * @param cell 
     * @param stereo - preserve stereo modules
     */
    static unsplit (cell:IChainCell) : IChainCell {
        if (cell.split) {
            cell.split = false
            // DISCARD right-channel
            cell.items.forEach(item => {
                item.split = false
                const {L} = item
                // UNSPLIT STEREO module? SPLIT and keep LEFT channel
                if (L && L.stereo && L.id === item.R?.id) {
                    item.L = SubModule.split(L)!.L
                }
                // DISCARD right channel
                item.R = undefined
            })
        }
        return cell
    }

    /**
     * Respective channel defined as a cell item?
     * @param cell 
     * @param chan 
     * @returns 
     */
    static hasChannel (cell:IChainCell, chan: TItemChannel) : boolean {
        if (cell.split && chan === 'R') {
            return false
        }
        return cell.items.some(item => item[chan])
    }

    static isValid (cell: IChainCell) : boolean {
        // Any state of a proc cell is valid
        // XXX (NOT true if holding an invalid module)
        return cell.proc
            ||
            ChainCellItem.isWhole(cell.items[0])
            && (
                !cell.ab
                ||
                cell.ab && (
                    cell.items.length === 2 && ChainCellItem.isStub(cell.items[1])
                    ||
                    cell.items.every(_ => ChainCellItem.isWhole(_))
                )
            )
    }

    static isStereo (cell: IChainCell) : boolean {
        return !cell.split && cell.items
            .filter(item => !ChainCellItem.isEmpty(item))
            .every(ChainCellItem.isStereo)
    }

    static isMono (cell: IChainCell) : boolean {
        return !cell.split && cell.items
            .filter(item => !ChainCellItem.isEmpty(item))
            .every(ChainCellItem.isMono)
    }

    static isConjoined (cell: IChainCell) : boolean {
        return cell.split && cell.items
            .filter(item => !ChainCellItem.isEmpty(item))
            .every(ChainCellItem.isConjoined)
    }

    /**
     * EXTRACT ACTIVE cell nodes, if any
     * @param cell 
     */
    static getActive (cell: IChainCell) : TModulePair | undefined {
        const {active:{L,R}} = cell.state
        if (L != null || cell.split && R != null) {
            const active = {
                L: L != null ? cell.items[L]?.L : undefined,
                R: R != null && !ChainCell.hasM2S(cell) && cell.split ? cell.items[R]?.R : undefined,
            }
            // STEREO? convert to left/right-side MONO node
            if (active?.L?.stereo) {
                active.L = SubModule.split(active.L)!.L
            }
            if (active?.R?.stereo) {
                active.R = SubModule.split(active.R)!.R
            }
            if (active.L || active.R) return active
        }
    }

    /**
     * ACTIVATE cell, if inactive
     * OPTIONALLY, target channel to activate
     */
    static activate (cell: IChainCell, channel?:TItemChannel) {
        if (!ChainCell.isValid(cell)) return
        // ALREADY active?
        const active = ChainCell.getActive(cell)
        if (active && (!channel || active[channel])) return
        // AB-group
        if (cell.ab) {
            // ACTIVATE first defined item of L/R channel
            // ACTIVATE LEFT-side
            if (!channel || channel === 'L') {
                const index = cell.items.findIndex(({L}) => L)
                if (index >= 0) {
                    cell.state.active.L = index
                }
            }
            // ACTIVATE RIGHT-side
            if (cell.split && (!channel || channel === 'R')) {
                const index = cell.items.findIndex(({R}) => R)
                if (index >= 0) {
                    cell.state.active.R = index
                }
            }
        }
        else {
            // ACTIVATE L/R channel
            if ((!channel || channel === 'L') && cell.items[0].L) {
                cell.state.active.L = 0
            }
            if (cell.split && !ChainCell.hasM2S(cell) && (!channel || channel === 'R') && cell.items[0].R) {
                cell.state.active.R = 0
            }
        }
    }

    static isStub (cell: IChainCell) : boolean {
        return cell.items.length === 1 && ChainCellItem.isEmpty(cell.items[0])
    }

    static isEmpty (cell: IChainCell) : boolean {
        return cell.items.every(ChainCellItem.isEmpty)
    }

    /**
     * Create AB-group cell from existing cell
     */
    static createAB (cell:IChainCell) : IChainCell {
        cell.items.push(ChainCellItem.createStub(cell.split))
        return {
            ...cell,
            ab:true,
            state: {
                ...cell.state,
                active: cell.proc
                    ? cell.state.active
                    // one item of S/D A/B module must be active
                    : {L: 0, R: 0}
            }
        }
    }

    static createStub ({
        id,
        dest,source,proc,
        ab,
        split,
    } : {
        id: string
        dest?: boolean
        source?: boolean
        proc?: boolean
        ab?: boolean
        split: boolean
    }) : IChainCell {
        return {
            id,
            ...Roled.create({source,proc,dest}),
            ab,
            items: [ChainCellItem.createStub(split)],
            state: ChainCellState.create({}),
            split,
        }
    }
}

export interface IChainLane {
    id: string
    cells: IChainCell[]
    [key: number]: undefined
}

export class ChainLane {
    static create ({id,cells:$cells,split}
        : {id:string|(()=>string),cells:IChainCell[],split:boolean})
        : IChainLane {

        const cells = $cells != null ? $cells! : [
            ChainCell.create({id,source:true,split}),
            ChainCell.create({id,dest:true,split})
        ]
        return {
            id: typeof id === 'string' ? id : id(),
            cells,
        }
    }

    static split (lane:IChainLane) {
        lane.cells.forEach(ChainCell.split)
    }

    /**
     * 
     * @param lane 
     * @param stereo - preserve stereo modules
     */
    static unsplit (lane:IChainLane) {
        lane.cells.forEach(cell => ChainCell.unsplit(cell))
    }

    static isSplit (lane:IChainLane) : boolean {
        return lane.cells.some(({split}) => split)
    }

    static isStereo (lane:IChainLane) : boolean {
        return lane.cells.every(({split}) => !split)
            &&
            lane.cells
            .filter(cell => !ChainCell.isStub(cell))
            .every(ChainCell.isStereo)
    }

    static isMono (lane:IChainLane) : boolean {
        return lane.cells.every(({split}) => !split)
            &&
            lane.cells
            .filter(cell => !ChainCell.isStub(cell))
            .every(ChainCell.isMono)
    }

    static isConjoined (lane:IChainLane) : boolean {
        return lane.cells
            .filter(cell => !ChainCell.isStub(cell))
            .every(ChainCell.isConjoined)
    }

    static valid (lane:IChainLane) : boolean {
        return lane.cells.every(cell => ChainCell.isValid(cell))
    }

    /**
     * EXTRACT all modules of lane
     * 
     * @param lane chain lane instance
     * @param split split any stereo module, when encountered
     * @returns 
     */
    static modules (lane:IChainLane,split?:boolean) : IModule[] {
        return lane.cells
        // EXTRACT device blocks from cell items
        .map(({items}) => items.map(item => ChainCellItem.modules({item,split})).flat())
        // FLATTEN to array of modules
        .flat().map(_ => _!)
    }
}

export interface IChain {
    id: string
    state: IChainState
    cond: IChainCondition
    lanes: IChainLane[]
    [key: number]: undefined
}

export interface IChainNode {
    cell: IChainCell
    prev?: IChainNode
    next: IChainNode[]
    lane: IChainLane
}

export type ChainNodeVisitor = (node?:IChainNode) => boolean
export type ChainNodeVisitorUp = (node?:IChainNode,prev?:IChainNode) => boolean

export class ChainNode {
    // VISIT all ChainCells upstream of node
    static visitUp (node: IChainNode,visitor:ChainNodeVisitorUp) {
        function visit (node:IChainNode) {
            if (!node.prev?.cell) {
                visitor(undefined)
            }
            // VISIT
            else if (!visitor(node.prev,node)) {
                // CANCELED by visitor
                return
            }
            // VISIT next
            else {
                visit(node.prev)
            }
        }
        visit(node)
    }

    // VISIT all ChainCells downstream of node
    static visitDown (node: IChainNode,visitor:ChainNodeVisitor) {
        function visit (node:IChainNode) : boolean {
            if (visitor(node)) {
                for (const next of node.next) {
                    // CANCELLED
                    if (!visit(next)) {
                        return false
                    }
                }
                return true
            }
            // CANCELLED
            else {
                return false
            }
        }
        visit(node)
    }

    // FIND node containing cell by ID
    static findCell (root:IChainNode,cell: string|((cell: IChainCell) => boolean)) : IChainNode | undefined {
        function dive (node:IChainNode) : IChainNode | undefined {
            if (typeof cell === 'string') {
                // FOUND
                if (node.cell.id === cell) {
                    return node
                }
                for (const next of node.next) {
                    const res = dive(next)
                    if (res) return res
                }
            }
            else {
                // FOUND
                if (cell(node.cell)) {
                    return node
                }
                for (const next of node.next) {
                    const res = dive(next)
                    if (res) return res
                }
            }
        }
        return dive(root)
    }
}

export class Chain {
    /**
     * VALIDATE chain has POTENTIALLY at least one active signal path (from source to dest)
     * AND all nodes VALID for current architecture
     * AND no node CONFLICT 
     * @param chain 
     * @returns 
     */
    static invalid (chain: IChain, arch: IArch) : Chain.Invalid | false {
        const graph = Chain.graph(chain)!
        let valid = false

        // VALIDATE at leaast one complete signal path exists (active or not)
        const continuous = ['L','R'].some(chan => {
            ChainNode.visitDown(graph, node => {
                if (!node) return false // END
                if (node.cell.dest && node.cell.items.some(item => item[chan])) {
                    ChainNode.visitUp(node, node => {
                        if (!node) return false // END
                        const {cell} = node
                        if (chan === 'R' && !cell.split) return false
                        if (cell.source) {
                            if (cell.items.some(item => item[chan])) {
                                valid = true
                            }
                            return false
                        }
                        // M2S?
                        else if (ChainCell.hasM2S(cell)) {
                            // "merge" the channels we are inspecting - we have gone from stereo (split) to mono
                            // ...so discard that we started on the right side (if we did)
                            chan = 'L'
                            return true
                        }
                        else {
                            return cell.items.some(item => item[chan])
                        }
                    })
                }
                return true
            })
            return valid
        })
        if (!continuous) {
            return Chain.Invalid.NoPath
        }
        // ARCHITECTURE-specific validations
        else {
            // VALIDATE all nodes well-defined and compatible with architecture
            if (Chain.nodes(chain).some(_ => Module.invalid(_,arch))) {
                return Chain.Invalid.BadNode
            }
            // VALIDATE no *active* conflicting nodes
            else if (Chain.activeNodes(chain)
                .some((a,_,nodes) => nodes.some(b => Module.conflicting(a,b,arch)))) {
                return Chain.Invalid.Conflict
            }
        }
        // VALID
        return false
    }

    // TOKENIZE chain title + decvices + modules' names
    static tokenize (self:IChain) : string[] {
        return [
            self.state.name.split(/\W+/).map(_ => _.toLowerCase()),
            Chain.nodes(self)
                .map(
                    ({name,device}) => [name,device?.name||'']
                    .join(' ')
                    .split(/\W+/)
                    .map(_ => _.toLowerCase()))
                .flat()
        ].flat()
    }

    /**
     * VALIDATE chain has at least one ACTIVE signal path (from source to dest)
     * @param chain 
     * @returns 
     */
    static hasContinuity (chain: IChain, arch: IArch) : boolean {
        if (Chain.invalid(chain,arch)) return false
        const graph = Chain.graph(chain)!
        // FLAG at least one active destination is connected to chain (active) source
        let cont = false
        return ['L','R'].some(chan => {
            ChainNode.visitDown(graph, node => {
                if (!node) return false // END
                const active = ChainCell.getActive(node.cell)
                if (node.cell.dest && active && active[chan]) {
                    // MOVE up "branch" until being terminated by inactive node or reaching chain's source
                    ChainNode.visitUp(node, (node,prev) => {
                        if (!node) return false // END
                        const {cell} = node
                        const active = ChainCell.getActive(cell)
                        if (cell.source) {
                            if (active && active[chan]) {
                                cont = true
                            }
                            return false
                        }
                        // M2S?
                        else if (ChainCell.hasM2S(cell)) {
                            // DISABLED M2S blocks all R-side signal
                            if (!active?.L && chan === 'R') {
                                return false
                            }
                            // "merge" channels we are inspecting - we have gone from stereo (split) to mono
                            // ...so discard that we started on the right side (if we did)
                            chan = 'L'
                            return true
                        }
                        // PROC
                        else {
                            if (active && active[chan]) {
                                return true
                            }
                            // IF `prev` ORIGINATES from another lane this processor is in effect
                            // a source, and if "off", cannot pass sigal
                            // (i.e. is an inactive mult source)
                            else {
                                return !prev || prev.lane.id === node.lane.id
                            }
                        }
                    })
                    return false
                }
                return true
            })
            return cont
        })
    }

    /**
     * REPORT cell's bypass state (indirectly blocked by an upstream inactive cell)
     * @param chain 
     * @returns 
     */
    static bypassState (chain: IChain, cell: IChainCell) : TStatusPair {
        // SOURCE CANNOT be BYPASSED
        const graph = Chain.graph(chain)!

        function bypassed (channel: TItemChannel) : boolean {
            // INACTIVE CHAIN bypasses everything
            if (!chain.cond.active) return true

            // SOURCE can't be bypassed (?)
            if (cell.source) return false

            // BY DEFAULT assume bypassed
            let bypassed = true

            // CRAWL up "branch" inspecting all intermediary nodes as needed
            ChainNode.visitDown(graph, node => {
                if (!node) return false // END
                // NO VALID destination in this branch
                if (node.cell.id === cell.id) {
                    // MOVE UP "branch" until terminated by inactive node or chain's source
                    ChainNode.visitUp(node, (node,prev) => {
                        if (!node) return false // END
                        const {cell} = node
                        const active = ChainCell.getActive(cell)
                        if (cell.source) {
                            if (active && active[channel]) {
                                bypassed = false
                            }
                            return false
                        }
                        // M2S?
                        else if (ChainCell.hasM2S(cell)) {
                            // DISABLED M2S blocks all R-side signal
                            if (!active?.L && channel === 'R') {
                                return false
                            }
                            // MERGE channels we are inspecting - we have gone from stereo (split) to mono
                            // ...so discard that we started on the right side (if we did)
                            channel = 'L'
                            return true
                        }
                        // PROC
                        else {
                            if (active && active[channel]) {
                                return true
                            }
                            // IF `prev` ORIGINATES from another lane this processor is in effect
                            // a source, and if "off", cannot pass sigal
                            // (i.e. is an inactive mult source)
                            else {
                                return !prev || prev.lane.id === node.lane.id
                            }
                        }
                    })
                    return false
                }
                return true
            })
            return bypassed
        }

        return cell.split
            ? {
                L: bypassed('L'),
                R: bypassed('R'),
            }
            : {
                L: bypassed('L'),
                R: false,
            }
    }


    /**
     * MODIFY chain to be "continuous" (at least one active signal path)
     * @param chain 
     */
    static continue (chain: IChain,arch: IArch) : IChain | undefined {
        // INVALID?!
        if (Chain.invalid(chain,arch)) return
        const graph = Chain.graph(chain)!

        // FIND left-most VALID destination node
        // CRAWL up "branch" activating all intermediary nodes as needed
        // RESPECT destination channel (if L or R channel is explicitly active)
        ChainNode.visitDown(graph, node => {
            if (!node) return false // END
            // NO VALID destination in this branch
            if (node.cell.dest && ChainCell.isValid(node.cell)) {
                const {L,R} = ChainCell.getActive(node.cell) || {}
                // TARGETED channel to activate (driven by activation state of DEST)
                let channel: TItemChannel | undefined = L && !R ? 'L' : !L && R ? 'R' : undefined
                // ACTIVATE DEST
                ChainCell.activate(node.cell,channel)
                // MOVE up "branch" until reaching chain's source
                ChainNode.visitUp(node, node => {
                    if (!node) return false // BACK TO SOURCE - DONE
                    const {cell} = node
                    // NEXT (UP)
                    const next = node.prev
                    // FIXUP target channel if traversing a MONO-TO-STEREO boundary
                    if (next && ChainCell.hasM2S(next.cell)) {
                        channel = undefined
                    }
                    ChainCell.activate(cell,channel)
                    return true
                })
                // ESCAPE once TARGET BRANCH has been TRAVERSED
                return false
            }
            return true
        })
        const $chain = clone(chain)
        $chain.lanes = Chain.ungraph(graph)!
        // SUCCEEDED?
        if (Chain.hasContinuity($chain,arch)) {
            return $chain
        }
    }

    static stereo (chain: IChain) : boolean {
        return !this.empty(chain) && chain.lanes.every(ChainLane.isStereo)
    }

    static isSplit (chain: IChain) : boolean {
        return chain.lanes.some(ChainLane.isSplit)
    }

    static mono (chain: IChain) : boolean {
        return !this.empty(chain) && chain.lanes.every(ChainLane.isMono)
    }

    static empty (chain: IChain) : boolean {
        return chain.lanes.length === 1 && chain.lanes[0].cells.every(ChainCell.isStub)
    }

    /** INDEX all ports of chain */
    // EXTRACT all modules from chain
    static nodes (chain:IChain,split?:true) : IModule[] {
        return chain.lanes
            .map(lane => ChainLane.modules(lane,split))
            // FLATTEN into single array of modules
            .flat()
    }

    static cells (chain: IChain) : IChainCell[] {
        return chain.lanes
            .map(({cells}) => cells)
            // FLATTEN into single array of modules
            .flat()
    }

    // ONE or MORE M2S modules exist in chain?
    static hasM2S (chain: IChain) : boolean {
        return !!Chain.nodes(chain).find(({m2s}) => m2s)
    }

    /**
     * COPY chain, assigning new identifiers where needed
     * @param chain 
     * @param idgen 
     * @returns
     */
    static copy (chain: IChain,idgen: ()=>string) : IChain {
        const dupe = clone(chain) as IChain
        // SOME CELL IDs are repeated. MUST carefully remap these in tandem.
        const cellIDs = {}
        dupe.id = idgen()
        dupe.lanes.forEach(lane => {
            lane.id = idgen()
            lane.cells.forEach(cell => {
                if (!cellIDs[cell.id]) {
                    cellIDs[cell.id] = idgen()
                }
                cell.id = cellIDs[cell.id]
            })
        })
        return dupe
    }

    /**
      * EXTRACT downstream dependents (i.e. lanes), direct and indirect, of (multed) source cell
      */
    static extractMultGraph ({chain,source}:{chain:IChain,source:IChainCell}) : IChainLane[] | undefined
    {
        // LANES to be extracted
        const lanes:Array<{lane:IChainLane,i:number}> = []
        function extract (cell:IChainCell) {
            chain.lanes.forEach((lane,i) => {
                if (lane.cells[0].id === cell.id) {
                    // RECURSIVELY follow other multed-sources of lane
                    for (const cell of lane.cells.slice(1)) {
                        extract(cell)
                    }
                    if (i !== 0) lanes.push({lane,i})
                }
            })
        }
        extract(source)
        return lanes.length ? lanes.sort((a,b) => a.i - b.i).map(({lane}) => lane) : undefined
    }

    static hasNode ({chain,module}:{chain:IChain,module:IModule}) : boolean {
        // IF TEST GROUP STEREO, we need to decompose it to avoid false negatives
        // when chain contains only L or R channel of its pair
        const $modules = module.stereo
            ? Object.values(SubModule.split(module)!)
            : [module]
        const modules = Chain.nodes(chain,true)
        return modules.some(({id}) => $modules.some(module => module.id === id))
    }

    static findNode ({chain,node}:{chain:IChain,node:string}) : IModule | undefined {
        const modules = Chain.nodes(chain)
        return modules.find(({id}) => id === node)
    }

    /**
     * Convert entirety of chain to split lanes
     * @param chain 
     */
    static split (chain:IChain) : IChain {
        D('SPLIT')
        chain.lanes.forEach(ChainLane.split)
        return chain
    }

    /**
     * Convert entirety of chain to unsplit lanes
     * @param chain 
     */
    static unsplit (chain: IChain) : IChain {
        D('UNSPLIT')
        chain.lanes.forEach(lane => ChainLane.unsplit(lane))
        return chain
    }

    // INDEX all modules from chain
    static indexNodes (chain:IChain,split?:true) : Record<string,IModule> {
        return this.nodes(chain,split)
            .reduce((p,c) => {
                if (!p[c.id]) {
                    p[c.id] = c
                }
                return p
            },{})
    }

    /** INDEX all ports of chain */
    static getPorts (chain:IChain,arch:IArch) : { from: {}, to: {} }
    {
        return Chain.nodes(chain)
            .reduce((p:{from:{},to:{}},module) => {
                const ports = Module.ports(module,arch)
                ports.from.forEach(port => p.from[port] = port)
                ports.to.forEach(port => p.to[port] = port)
                return p
            },{from:{},to:{}})
    }

    /**
     * EXISTS one or more processor nodes
     * @param chain
     * @returns true if >0 proc nodes
     */
    static hasProcs (chain: IChain) : boolean {
        return Chain.nodes(chain).some(_ => _.proc)
    }

    static insertNode ({chain,module,idgen,arch}:{chain: IChain, module: IModule, idgen: ()=>string,arch:IArch})
        : {chain:string,cell:string,item:number,channel:'L'|'R'} | undefined
    {
        const hasM2S = Chain.hasM2S(chain)
        const {source,proc} = module

        const chainPorts = Chain.getPorts(chain,arch)
        const modPorts = Module.ports(module,arch)
        if (modPorts.from.some(port => chainPorts.from[port])
            || modPorts.to.some(port => chainPorts.to[port])) {
            D('insertNode: PORT CONFLICT')
            return
        }
        // CANNOT insert M2S into chain w/ existing M2S
        else if (module.m2s && hasM2S) {
            return
        }
        // CANNOT assume insertion point in multi-lane chain
        else if (chain.lanes.length > 1) {
            return
        }
        // CANNOT have stereo module as source of M2S chain
        else if (hasM2S && source && module.stereo) {
            return
        }

        const mono = Chain.mono(chain)
        const stereo = Chain.stereo(chain)
        // INCOMPATIBLE PHONICS?
        // mono-module to pure-stereo, or stereo-module to pure mono, or mono-to-stereo
        const needSplit = !Chain.isSplit(chain) && (module.stereo || module.m2s)

        const lane = chain.lanes[0]
        const {cells} = lane

        // PURE MONO chain
        if (!Chain.isSplit(chain) && !needSplit) {
            if (proc) {
                // LOCATE index of first empty, or partial empty (in split case) non-AB cell, if any
                const vac = 
                    cells
                        .filter(({proc}) => proc)
                        // DISCOVER VACANCY w/in existing cell
                        .find(cell => cell.items.some(ChainCellItem.isStub))

                // NO VACANCIES - INSERT new CELL
                if (!vac) {
                    const cell = ChainCell.create({
                        id: idgen(),
                        proc: true,
                        state: ChainCellState.create(module),
                        items: [ChainCellItem.create({L:module,split:false})],
                        split: false,
                    })
                    // APPEND
                    cells.splice(cells.length - 1,0,cell)
                    return {chain:chain.id,cell:cell.id,item:0,channel:'L'}
                }
                // VACANCY
                else {
                    // VACANT item index
                    const itemIdx = vac.items.findIndex(item => ChainCellItem.isStub(item))
                    vac.items[itemIdx] = ChainCellItem.create({L:module,split:false})
                    return {chain:chain.id,cell:vac.id,item:itemIdx,channel:'L'}
                }
            }
            // SOURCE/DEST replace or append
            else {
                const split = false
                const cellIndex = source ? 0 : cells.length - 1
                // FIRST *or* LAST cell of chain
                const cell = cells[cellIndex]
                const {ab,items} = cell

                // VACANT item index
                const itemIdx = cell.items.findIndex(ChainCellItem.isStub)

                // APPEND to AB-group
                if (ab) {
                    // APPEND
                    if (itemIdx < 0) {
                        items.splice(cell.items.length, 0,ChainCellItem.create({L:module,split}))
                        return {
                            chain: chain.id,
                            cell: cell.id,
                            item: items.length - 1,
                            channel: 'L',
                        }
                    }
                    // POPULATE stub
                    else {
                        const item = items[itemIdx]
                        item.L = module
                        return {chain:chain.id,cell:cell.id,item:itemIdx,channel:'L'}
                    }
                }
                else {
                    // REPLACE EXISTING CELL
                    if (itemIdx < 0) {
                        const cell = ChainCell.create({
                            id: idgen(),
                            state: ChainCellState.create(module),
                            items: [ChainCellItem.create({L:module,split})],
                            ...Role.create(module.role),
                            split,
                        })
                        cells.splice(cellIndex,1,cell)
                        return {chain:chain.id,cell:cell.id,item:0,channel:'L'}
                    }
                    else {
                        const item = items[itemIdx]
                        item.L = module
                        return {chain:chain.id,cell:cell.id,item:0,channel:'L'}
                    }
                }
            }
        }
        else {
            // INSERT procs
            if (proc) {
                // SPLIT
                if (needSplit) Chain.split(chain)
                // LOCATE index of first empty, or partial empty (in split case) non-AB cell, if any
                const vac = 
                    cells
                        .filter(({proc}) => proc)
                        // DISCOVER VACANCY w/in existing cell
                        .find(cell => 
                            // LEFT side of M2S-module cell will appear vacant unless we explicitly disqualify
                            !ChainCell.hasM2S(cell)
                            && 
                            cell.items.some(({L,R,split}) =>
                                module.stereo
                                ? split && (!L || !needSplit && !R)
                                : !L || split && !needSplit && !R
                            )
                        )

                // NO VACANCIES - INSERT NEW CELL
                if (!vac || module.m2s) {
                    const split = true
                    const insertee =
                        ChainCellItem.create(
                            module.mono
                            ? {L: module,split}
                            : {L: module,R:module,split})

                    const cell = ChainCell.create({
                        id: idgen(),
                        proc: true,
                        state: ChainCellState.create(module),
                        items: [insertee],
                        split,
                    })

                    // INSERT M2S ATOP SPLIT lane (if possible)
                    if (module.m2s && !needSplit) {
                        cells.splice(1,0,cell)
                    }
                    // ...or APPEND
                    else {
                        cells.splice(cells.length - 1,0,cell)
                    }

                    // ADJUST cell split-states as required by insertion of M2S processor
                    if (module.m2s) {
                        Chain.reconcile(chain)
                    }
                    return {chain:chain.id,cell:cell.id,item:0,channel:'L'}
                }
                // VACANCY (in L or R channel)
                else {
                    // VACANT item index
                    const itemIdx = vac
                        .items
                        .findIndex(({L,R,split}) => !L || split && !needSplit && !R)
                    const item = vac.items[itemIdx]
                    const channel = !item.L || module.stereo  ? 'L' : 'R'
                    // REPLACE entire item with stereo module
                    if (module.stereo) {
                        vac.items[itemIdx] = ChainCellItem.create({L:module,R:module,split:true})
                    }
                    // POPULATE L or R side of split item w/ mono module
                    else {
                        item[channel] = module
                    }
                    return {chain:chain.id,cell:vac.id,item:itemIdx,channel}
                }
            }
            // SOURCE/DEST replace or append
            else {
                const cellIndex = source ? 0 : cells.length - 1
                // FIRST of LAST cell in the chain
                const cell = cells[cellIndex]
                const {ab,items} = cell

                // SOURCE of M2S chain is always MONO/UNSPLIT
                const split = !(source && hasM2S)

                // VACANT item index
                const itemIdx = items.findIndex(({L,R}) => !L || split && !needSplit && !R)

                // SPLIT
                if (needSplit) Chain.split(chain)

                // APPEND to AB-group
                if (ab) {
                    // APPEND
                    if (itemIdx < 0) {
                        items.splice(cell.items.length, 0, 
                            ChainCellItem.create(
                                module.stereo
                                ? {L:module,R:module,split:true}
                                : {L:module,split})
                        )
                        return {
                            chain: chain.id,
                            cell: cell.id,
                            item: items.length - 1,
                            channel: 'L',
                        }
                    }
                    // POPLATE vacant L/R channel
                    else {
                        const item = items[itemIdx]
                        const channel = !item.L || module.stereo ? 'L' : 'R'
                        // REPLACE ENTIRE ITEM
                        if (module.stereo) {
                            items[itemIdx] = ChainCellItem.create({L:module,R:module,split:true})
                        }
                        // POPULATE vacant item channel
                        else {
                            item[channel] = module
                        }
                        return {chain:chain.id,cell:cell.id,item:itemIdx,channel}
                    }
                }
                else {
                    // REPLACE a WHOLE CELL
                    if (itemIdx < 0) {
                        const cell = ChainCell.create({
                            id: idgen(),
                            state: ChainCellState.create(module),
                            items: [
                                ChainCellItem.create(
                                    module.stereo
                                    ? {L:module,R:module,split:true}
                                    : {L:module,split})
                            ],
                            ...Role.create(module.role),
                            split: module.stereo || split,
                        })
                        cells.splice(cellIndex,1,cell)
                        return {chain:chain.id,cell:cell.id,item:0,channel:'L'}
                    }
                    else {
                        const item = items[itemIdx]
                        const channel = !item.L || module.stereo ? 'L' : 'R'
                        // REPLACE entire item with stereo module
                        if (module.stereo) {
                            items[itemIdx] = ChainCellItem.create({L:module,R:module,split:true})
                        }
                        // POPULATE L or R side of split item w/ mono module
                        else {
                            item[channel] = module
                        }
                        return {chain:chain.id,cell:cell.id,item:itemIdx,channel}
                    }
                }
            }
        }
    }

    /**
     * Reconcile any removal/placement of M2S module(s)
     * @param chain 
     */
    static reconcile (chain: IChain) {
        // FIX things up, ensure all cells UPSTREAM of M2S module are NON-SPLIT, all DOWNSTREAM are SPLIT
        if (Chain.hasM2S(chain)) {
            // CONVERT chain to graph
            const $graph = Chain.graph(chain)!
            // UNSPLIT all cells UPSTREAM of M2S module
            function diveUpstream (node:IChainNode) {
                const {cell} = node
                // UNSPLIT + MONO-IFY all cells UPSTREAM of M2S module
                if (!cell.items[0].L?.m2s) {
                    ChainCell.unsplit(cell)!
                    node.next.forEach(diveUpstream)
                }
                // EVERYTHING BELOW - SPLIT
                else {
                    node.next.forEach(diveDownstream)
                }
            }
            // SPLIT all cells DOWNSTREAM of M2S module
            function diveDownstream (node:IChainNode) {
                const {cell} = node
                ChainCell.split(cell)
                // EVERYTHING BELOW - SPLIT
                node.next.forEach(diveDownstream)
            }
            diveUpstream($graph)
            const lanes = Chain.ungraph($graph)
            chain.lanes = lanes
        }
    }

    // REMOVE EMPTY cells (and deps)
    static collapse (chain: IChain) : IChainLane[] {
        // CONVERT chain to graph
        const graph = Chain.graph(chain)!

        function filter (cell :IChainCell) {
            // EMPTY SOURCE/DEST AB-group
            if (ChainCell.isEmpty(cell)) {
                // REMOVE empty proc cell
                if (cell.proc) {
                    return false
                }
                // DOWNGRADE empty AB-group (of proc/dest)
                else if (cell.ab) {
                    cell.ab = false
                    cell.items.length = 1
                }
            }
            return true
        }
        return Chain.ungraph(graph,filter)
    }

    static graph (chain: IChain) : IChainNode | undefined {
        const {lanes} = chain
        if (!lanes.length || !lanes[0].cells.length) return undefined
        const seen:Record<string,IChainNode> = {}
        const rootCell = lanes[0].cells[0]
        const root = seen[rootCell.id] = {
            cell: rootCell,
            next: [],
            lane: lanes[0],
        }
        lanes.forEach(lane => {
            const {cells} = lane
            const source = cells[0]
            // PREDECESSOR
            let prev = seen[source.id]
            cells.forEach((cell,j) => {
                // SKIP source
                if (j === 0) return
                const next: IChainNode = {
                    prev,
                    cell,
                    next: [],
                    lane,
                }
                seen[cell.id] = next
                prev.next[prev.next.length] = next
                prev = next
            })
        })
        return root
    }

    static ungraph (root: IChainNode,filter?: (cell:IChainCell) => boolean ) : IChainLane[] {
        const lanes:IChainLane[] = []
        function dive (node: IChainNode, lane: IChainLane,depth:number) {
            const {cell} = node
            // FILTER OUT cell?
            if (!filter || filter(cell)) {
                lane.cells[lane.cells.length] = cell
            }
            node.next.forEach((next,i) => {
                // NODE of SAME LANE
                if (i === 0) {
                    dive(next,lane,depth + 1)
                }
                // MULT NODE (node is source)
                else if (!filter || filter(cell)) {
                    // ADD new lane
                    const lane = {...next.lane}
                    lane.cells = [cell]
                    lanes[lanes.length] = lane
                    dive(next,lane,1)
                }
            })
        }
        const lane = {...root.lane}
        lane.cells = []
        lanes[0] = lane
        dive(root,lane,0)
        return lanes
    }

    /**
     * Decompose chain to a collection of I/O segments (source->sink)
     */

    static decompose (chain:IChain,arch:IArch) : IRoute[] {
        const pairs: IRoute[] = []
        // CHAIN has no active/valid signal paths?
        if (!Chain.hasContinuity(chain,arch)) return []
        // CONVERT chain "matrix" form to graph
        const node = Chain.graph(chain)
        if (!node) return []

        // CHAIN ROOT
        dive({node})

        return pairs

        function dive ({
                node,
                source,
            }:
            {
                node: IChainNode
                source?: {
                    L?: number
                    R?: number
                }
            }
        )
        {
            const {cell} = node
            // EXTRACT ACTIVE nodes
            // (stereo nodes split to L/R mono nodes)
            const {L:$L,R:$R} = ChainCell.getActive(cell) || {}
            const L: { to?: IChannel, from?: IChannel } = $L ? { to: $L.L.to, from: $L.L.from } : {}
            const R: { to?: IChannel, from?: IChannel } = 
                $R 
                ? { to: $R.L.to, from: $R.L.from }
                // M2S CASE: `getActive` does not return
                : $L?.R && ChainCell.hasM2S(cell)
                ? { from: $L.R.from }
                : {}

            // ESTABLISH SINKS (that we plug source from above, INTO)
            if ($L || $R) {
                if (source?.L != null && L.to) {
                    pairs.push({
                        from: source.L, // SOURCE
                        to: L.to.port, // SINK
                    })
                }
                if (source?.R != null && R.to) {
                    pairs.push({
                        from: source.R, // SOURCE
                        to: R.to.port, // SINK
                    })
                }
            }
            // DEAD CHAIN (chain source is wholly inactive, END HERE
            else if (!source) {
                D('NO SOURCE')
                return
            }

            // ESTABLISH "source" PORT(s) for (next) DOWNSTREAM nodes
            if (!cell.dest) {
                if (source) {
                    source = {
                        L: L.from ? L.from.port : source.L,
                        R: R.from ? R.from.port : source.R,
                    }
                }
                // THIS IS CHAIN "source"
                else {
                    source = {
                        L: L.from?.port,
                        R: R.from?.port,
                    }
                }
                
                // DECOMPOSE downstream module(s)
                node.next
                    // IF source is INACIVE we only pass signal through its downstream sibling child node
                    // (mults are disabled)
                    .filter((_,i) => i === 0 || $L || $R)
                    .forEach(node => dive({source,node}))
            }
        }
    }

    /**
     * REFRESH node instances within chain
     * @param chain 
     * @param $nodes 
     */
    static refreshNodes (chain: IChain, $nodes: Record<string,IModule>) {
        // TRANSLATE stereo nodes to subnodes to navigate mono splits
        const nodes = Object
        .entries($nodes)
        .reduce((p,[id,module]) => {
            p[id] = module
            const split = SubModule.split(module)
            if (split) {
                const {L,R} = split
                p[L.id] = L
                p[R.id] = R
            }
            return p
        },{})

        // FIND and REFRESH all EMBEDDED node instances
        chain.lanes.forEach(lane => {
            lane.cells.forEach(cell => {
                cell.items.forEach(item => {
                    if (item.L) {
                        const {id} = item.L
                        if (!nodes[id]) {
                            E(`UNKNOWN left-side NODE ${id} in CHAIN ${chain.id}`)
                        }
                        else {
                            const {midi} = item.L
                            item.L = clone(nodes[id])
                            if (midi) {
                                item.L!.midi = {...midi}
                            }
                        }
                    }
                    if (item.R) {
                        const {id} = item.R
                        if (!nodes[id]) {
                            E(`UNKNOWN right-side NODE ${id} in CHAIN ${chain.id}`)
                        }
                        else {
                            const {midi} = item.R
                            item.R = clone(nodes[id])
                            if (midi) {
                                item.R!.midi = {...midi}
                            }
                        }
                    }
                })
            })
        })
    }

    /**
     * COLLECT all ACTIVE NODES of chain
     * @param chain 
     * @returns 
     */
    static activeNodes (chain: IChain) : IModule[] {
        const graph = Chain.graph(chain)!
        let cont = false
        const actives: Record<string,IModule> = {};
        (['L','R']).forEach(chan => {
            ChainNode.visitDown(graph, node => {
                if (!node) return false // END
                const active = ChainCell.getActive(node.cell)
                // NAVIGATE DOWN to "leaf" (destination)
                if (node.cell.dest && active && active[chan]) {
                    // ALL ACTIVE NODES comprising "circuit"
                    const circuit: Record<string,IModule> = {}
                    // INCLUDE the destination node itself: visitUp() begins at node.prev,
                    // so the destination's own ports would otherwise be omitted from the
                    // active set — silently hiding genuine conflicts on the chain's matrix
                    // output (e.g. FP15 colliding with a processor's D7 on PP64R).
                    circuit[active[chan].id] = active[chan]
                    // NOW NAVIGATE UP to "root" (source) COLLECTING active
                    ChainNode.visitUp(node, node => {
                        if (!node) return false // END
                        const {cell} = node
                        const active = ChainCell.getActive(cell)
                        if (active?.[chan]) {
                            // COLLECTIVE active node
                            circuit[active[chan].id] = active[chan]
                        }
                        // REACHED "root"
                        if (cell.source) {
                            // COPY COLLECTED active nodes
                            if (active?.[chan]) {
                                Object.assign(actives,circuit)
                            }
                            return false
                        }
                        // M2S?
                        else if (ChainCell.hasM2S(cell)) {
                            // DISABLED M2S blocks all R-side signal
                            if (!active?.L && chan === 'R') {
                                return false
                            }
                            // "merge" channels we are inspecting - we have gone from stereo (split) to mono
                            // ...so discard that we started on the right side (if we did)
                            chan = 'L'
                            return true
                        }
                        // PROC NODE
                        else {
                            return true
                        }
                    })
                    return false
                }
                return true
            })
        })
        return Object.values(actives)
    }

    /**
     * COLLECT all in-use (active) ports of chain
     * @param chain 
     * @param split 
     * @returns 
     */
    static activePorts (chain:IChain,arch:IArch) : { from: Record<string,IModule>, to: Record<string,IModule> }
    {
        return Chain.activeNodes(chain)
            .reduce((p:{from:{},to:{}},module) => {
                const ports = Module.ports(module,arch)
                ports.from.forEach(port => p.from[port] = module)
                ports.to.forEach(port => p.to[port] = module)
                return p
            },{from:{},to:{}})
    }

    /**
     * COLLECT chains whom have conflicting active ports with given chain
     * @param chain 
     * @param chains 
     * @returns 
     */
    static conflicts (chain: IChain,chains:IChain[],arch:IArch) : Array<{chain: IChain, nodes: IModule[]}> {
        const $chain = clone(chain)
        $chain.cond.active = true
        // INDEX all active ports of chain
        const ports = Chain.activePorts($chain,arch)
        if (!Object.entries(ports).length) return []

        // INDEX conflicting ports of other active chains
        return Object.values(
            chains
            .filter(({id}) => id !== chain.id) // only other chains
            .reduce((p,chain) => {
                const other = Chain.activePorts(chain,arch)
                const conflicts = {}
                Object
                    .keys(other.from)
                    // ONLY CONFLICTING ports
                    .filter(port => Object.keys(ports.from).some(p => arch.ports.isConflict(port, p)))
                    .forEach(port => {
                        conflicts[other.from[port].id] = other.from[port]
                    })
                Object
                    .keys(other.to)
                    // ONLY CONFLICTING ports
                    .filter(port => Object.keys(ports.to).some(p => arch.ports.isConflict(port, p)))
                    .forEach(port => {
                        conflicts[other.to[port].id] = other.to[port]
                    })
                // CONFLICTS FOUND?
                if (Object.keys(conflicts).length) {
                    p[chain.id] = {
                        chain,
                        nodes: Object.values(conflicts),
                    }
                }
                return p
            },{})
        )
    }
}

export namespace Chain {
    export enum Invalid {
        NoPath = 1,
        BadNode,
        Conflict,
    }
}

